import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { ReactNode } from 'react'
import { useLocation, useRouter } from '@tanstack/react-router'
import { MessageCircle, X } from 'lucide-react'

import { useAuth } from '@/lib/auth'
import { useDb } from '@/lib/dbContext'
import {
  resetUnreadIndicator,
  setUnreadIndicator,
} from '@/lib/documentIndicator'
import {
  countUnread,
  feedbackCacheKey,
  feedbackLastReadKey,
  fetchFeedback,
  fetchFeedbackMeta,
  isCacheFresh,
  latestMessageCreated,
  newAdminMessages,
  sendFeedbackMessage,
  type FeedbackApiResponse,
  type FeedbackCache,
  type FeedbackConversation,
  type FeedbackMessage,
} from '@/lib/feedback'
import {
  readLocalStorage,
  removeLocalStorage,
  writeLocalStorage,
} from '@/lib/localStorage'

const POLL_INTERVAL_MS = 20_000
const FLASH_DISMISS_MS = 8_000

export interface FeedbackContextValue {
  conversation?: FeedbackConversation
  messages: FeedbackMessage[]
  unreadCount: number
  /** Read watermark: created timestamp of the newest message the user has seen. */
  lastReadAt: string | null
  isLoading: boolean
  isSending: boolean
  error?: string
  sendMessage: (message: string) => Promise<void>
  markAllRead: () => void
}

const FeedbackContext = createContext<FeedbackContextValue>({
  conversation: undefined,
  messages: [],
  unreadCount: 0,
  lastReadAt: null,
  isLoading: false,
  isSending: false,
  error: undefined,
  sendMessage: async () => {},
  markAllRead: () => {},
})

export function useFeedback(): FeedbackContextValue {
  return useContext(FeedbackContext)
}

interface FlashState {
  message: FeedbackMessage
  count: number
  key: number
}

function FeedbackFlash(props: {
  flash: FlashState
  onOpen: () => void
  onDismiss: () => void
}) {
  const { flash, onOpen, onDismiss } = props

  useEffect(() => {
    const timeout = setTimeout(onDismiss, FLASH_DISMISS_MS)
    return () => clearTimeout(timeout)
  }, [onDismiss])

  const preview =
    flash.message.body.length > 120
      ? `${flash.message.body.slice(0, 117)}...`
      : flash.message.body

  return (
    <output
      aria-live="polite"
      className="fixed bottom-4 right-4 z-50 block w-80 max-w-[calc(100vw-2rem)] animate-in fade-in slide-in-from-bottom-4"
    >
      <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-lg">
        <div className="mt-0.5 rounded-full bg-blue-100 p-1.5 text-blue-600">
          <MessageCircle className="h-4 w-4" />
        </div>
        <button
          type="button"
          className="min-w-0 flex-1 cursor-pointer text-left"
          onClick={onOpen}
        >
          <p className="text-sm font-semibold text-slate-900">
            {flash.count > 1
              ? `${flash.count} new replies from the team`
              : 'New reply from the team'}
          </p>
          <p className="mt-0.5 line-clamp-3 text-sm text-slate-600">{preview}</p>
        </button>
        <button
          type="button"
          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          onClick={onDismiss}
          aria-label="Dismiss notification"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </output>
  )
}

export function FeedbackProvider(props: { children: ReactNode }) {
  const { user } = useAuth()
  const db = useDb()
  const router = useRouter()
  const location = useLocation()

  const [conversation, setConversation] = useState<FeedbackConversation>()
  const [messages, setMessages] = useState<FeedbackMessage[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<string>()
  const [lastReadAt, setLastReadAt] = useState<string | null>(null)
  const [flash, setFlash] = useState<FlashState | null>(null)

  const knownIdsRef = useRef<Set<string> | null>(null)
  /** Digest of the message list we currently hold; null forces a full fetch. */
  const digestRef = useRef<string | null>(null)
  const onAboutPageRef = useRef(false)
  onAboutPageRef.current = location.pathname === '/about'

  const userId = user?.id

  // Load the per-user read watermark, and keep it in sync across tabs.
  useEffect(() => {
    if (!userId) {
      setLastReadAt(null)
      return
    }
    const key = feedbackLastReadKey(userId)
    setLastReadAt(readLocalStorage<string | null>(key, null))

    const onStorage = (event: StorageEvent) => {
      if (event.key === key) {
        setLastReadAt(readLocalStorage<string | null>(key, null))
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [userId])

  const applyMessages = useCallback(
    (
      nextConversation: FeedbackConversation | undefined,
      nextMessages: FeedbackMessage[],
      options: { allowFlash: boolean },
    ) => {
      if (!userId) return
      setConversation(nextConversation)
      setMessages(nextMessages)

      // First data on a device that has never read anything: treat history as
      // read instead of badging months-old replies (there is no server-side
      // read state to consult).
      const key = feedbackLastReadKey(userId)
      if (readLocalStorage<string | null>(key, null) === null) {
        const latest = latestMessageCreated(nextMessages)
        if (latest !== null) {
          writeLocalStorage(key, latest)
          setLastReadAt(latest)
        }
      }

      const known = knownIdsRef.current
      if (known !== null && options.allowFlash && !onAboutPageRef.current) {
        const fresh = newAdminMessages(known, nextMessages)
        if (fresh.length > 0) {
          const newest = fresh[fresh.length - 1]
          setFlash((prev) => ({
            message: newest,
            count: fresh.length,
            key: (prev?.key ?? 0) + 1,
          }))
        }
      }
      knownIdsRef.current = new Set(nextMessages.map((m) => m.id))
    },
    [userId],
  )

  // Persist the freshly fetched snapshot so the next page load can render
  // instantly and the poller can skip fetches while the digest is unchanged.
  const storeSnapshot = useCallback(
    (data: FeedbackApiResponse | null, digest: string | null) => {
      if (!userId) return
      digestRef.current = digest
      if (data && digest) {
        writeLocalStorage<FeedbackCache>(feedbackCacheKey(userId), {
          digest,
          conversation: data.conversation,
          messages: data.messages,
        })
      } else {
        removeLocalStorage(feedbackCacheKey(userId))
      }
    },
    [userId],
  )

  const poll = useCallback(async () => {
    if (!userId) return
    try {
      // Cheap change check first; only fetch the full message list when the
      // server digest differs from the one we already hold.
      const meta = await fetchFeedbackMeta(db.authStore.token)
      if (meta && meta.conversation === null) {
        setError(undefined)
        storeSnapshot(null, null)
        applyMessages(undefined, [], { allowFlash: true })
        return
      }
      if (isCacheFresh(meta, digestRef.current, knownIdsRef.current !== null)) {
        setError(undefined)
        if (meta?.conversation) setConversation(meta.conversation)
        return
      }
      const data = await fetchFeedback(db.authStore.token)
      setError(undefined)
      applyMessages(data?.conversation, data?.messages ?? [], {
        allowFlash: true,
      })
      storeSnapshot(data, data?.digest ?? meta?.digest ?? null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load feedback')
    } finally {
      setIsLoading(false)
    }
  }, [userId, db, applyMessages, storeSnapshot])

  useEffect(() => {
    if (!userId) {
      setConversation(undefined)
      setMessages([])
      setFlash(null)
      setIsLoading(false)
      knownIdsRef.current = null
      digestRef.current = null
      return
    }

    knownIdsRef.current = null
    digestRef.current = null

    // Render the cached conversation immediately; the first poll then only
    // does a full fetch if the server digest differs from the cached one.
    const cached = readLocalStorage<FeedbackCache | null>(
      feedbackCacheKey(userId),
      null,
    )
    if (cached?.digest && Array.isArray(cached.messages)) {
      digestRef.current = cached.digest
      applyMessages(cached.conversation, cached.messages, { allowFlash: false })
      setIsLoading(false)
    } else {
      setIsLoading(true)
    }
    poll()

    // Poll even while the tab is hidden so a backgrounded tab's unread badge,
    // title, and favicon stay current — that persistent chrome is the main
    // signal when the in-app toast isn't visible.
    const interval = setInterval(poll, POLL_INTERVAL_MS)
    const onVisible = () => {
      if (!document.hidden) poll()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
  }, [userId, poll, applyMessages])

  const sendMessage = useCallback(
    async (message: string) => {
      if (!userId) return
      setIsSending(true)
      setError(undefined)
      try {
        const data = await sendFeedbackMessage(message, db.authStore.token)
        applyMessages(data.conversation, data.messages, { allowFlash: true })
        storeSnapshot(data, data.digest ?? null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to send message')
      } finally {
        setIsSending(false)
      }
    },
    [userId, db, applyMessages, storeSnapshot],
  )

  const markAllRead = useCallback(() => {
    if (!userId) return
    const latest = latestMessageCreated(messages)
    if (latest === null) return
    const key = feedbackLastReadKey(userId)
    const current = readLocalStorage<string | null>(key, null)
    if (current === null || latest > current) {
      writeLocalStorage(key, latest)
    }
    setLastReadAt((prev) => (prev === null || latest > prev ? latest : prev))
    setFlash(null)
  }, [userId, messages])

  const unreadCount = useMemo(
    () => (userId ? countUnread(messages, lastReadAt) : 0),
    [userId, messages, lastReadAt],
  )

  // Reflect unread messages in the tab title + favicon, so a waiting reply is
  // visible even when this tab is backgrounded. Restore the tab chrome on unmount.
  useEffect(() => {
    setUnreadIndicator(unreadCount)
  }, [unreadCount])
  useEffect(() => resetUnreadIndicator, [])

  const value = useMemo<FeedbackContextValue>(
    () => ({
      conversation,
      messages,
      unreadCount,
      lastReadAt,
      isLoading,
      isSending,
      error,
      sendMessage,
      markAllRead,
    }),
    [
      conversation,
      messages,
      unreadCount,
      lastReadAt,
      isLoading,
      isSending,
      error,
      sendMessage,
      markAllRead,
    ],
  )

  const dismissFlash = useCallback(() => setFlash(null), [])
  const openFlash = useCallback(() => {
    setFlash(null)
    router.navigate({ to: '/about' })
  }, [router])

  return (
    <FeedbackContext.Provider value={value}>
      {props.children}
      {flash ? (
        <FeedbackFlash
          key={flash.key}
          flash={flash}
          onOpen={openFlash}
          onDismiss={dismissFlash}
        />
      ) : null}
    </FeedbackContext.Provider>
  )
}
